import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header, { LanguageContext } from './components/Header';
import ChartForm from './components/ChartForm';
import Footer from './components/Footer';
import axios from 'axios';

// API requests are same-origin by default.
//
// In production the Express server serves the built client, so relative /api
// paths resolve directly. In development the CRA dev server proxies /api to
// the API server (see setupProxy.js), which also avoids CORS entirely.
//
// REACT_APP_API_URL remains available for deployments that host the API on a
// different origin, but it must be a URL the *browser* can resolve. Setting it
// to a Docker-internal hostname breaks the app, and overriding it here also
// bypasses the dev proxy.
if (process.env.REACT_APP_API_URL) {
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
}

function App() {
  const [tractateData, setTractateData] = useState({});
  const [perakimData, setPerakimData] = useState({});
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'he' : 'en');
  };

  useEffect(() => {
    // Fetch tractate data from the API
    const fetchTractateData = async () => {
      try {
        const [tractates, perakim] = await Promise.all([
          axios.get('/api/tractates'),
          // Perek boundaries only decorate the daf dropdowns; the form works
          // without them, so a failure here is not fatal.
          axios.get('/api/perakim').catch(() => ({ data: {} }))
        ]);
        setTractateData(tractates.data);
        setPerakimData(perakim.data || {});
        setLoading(false);
      } catch (error) {
        console.error('Error fetching tractate data:', error);
        toast.error('Failed to load tractate data');
        setLoading(false);
      }
    };

    fetchTractateData();
  }, []);

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage }}>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <ToastContainer 
          position="top-right" 
          autoClose={5000} 
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={language === 'he'}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastClassName="rounded-lg shadow-lg"
        />
        <Header />
        <main className="flex-grow container mx-auto px-4 py-12">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="mb-4 flex justify-center">
                <div className="relative">
                  <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-700 to-purple-700 inline-block">
                    Chazara Charts
                  </h1>
                  <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></div>
                </div>
              </div>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                Generate personalized Torah review charts for Gemara, Mishnayos and Mishna Berura.
              </p>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="relative">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-4 border-t-indigo-600"></div>
                  <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                    <div className="h-8 w-8 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            ) : (
              <ChartForm tractateData={tractateData} perakimData={perakimData} />
            )}
          </div>
        </main>
        <Footer />
      </div>
    </LanguageContext.Provider>
  );
}

export default App; 